"use client";

import { useTranslations } from "next-intl";
import SolarIcon from "@/components/ui/SolarIcon/SolarIcon";

export default function PortfolioFilRougeSection() {
    const t = useTranslations();

    return (
        <section id="projects" className="pt-16">
            <h2
                className="text-2xl font-bold dark:text-white font-sourceSans"
                style={{ marginBottom: "0.5em" }}
            >
                {t("PortfolioFilRougeSection_title")}
            </h2>
            <div className="w-20 h-1 bg-primary rounded mt-3 mb-5"></div>

            <p className="text-black dark:text-lightBlue max-w-[700px] leading-7 font-geistSans mb-0">
                {t.rich("PortfolioFilRougeSection_intro", {
                    strong: (chunks) => (
                        <strong className="font-semibold text-black dark:text-white">
                            {chunks}
                        </strong>
                    ),
                })}
            </p>

            <div className="mt-10 grid xl:grid-cols-[1.3fr_1fr] gap-8 xl:gap-12 items-center">
                {/* LEFT — Mockup portfolio dans browser frame */}
                <div className="relative">
                    {/* Glow décoratif derrière */}
                    <div className="absolute -inset-4 bg-gradient-to-br from-blue-200/30 via-indigo-200/20 to-purple-200/20 dark:from-blue-500/10 dark:via-indigo-500/10 dark:to-purple-500/10 rounded-3xl blur-2xl pointer-events-none" />

                    <div className="relative rounded-2xl ring-1 ring-gray-200 dark:ring-white/10 shadow-2xl overflow-hidden bg-white">
                        {/* Browser frame */}
                        <div className="flex items-center gap-2 px-4 py-3 bg-gray-50 border-b border-gray-100">
                            <div className="flex items-center gap-1.5 flex-shrink-0 w-[52px]">
                                <span className="w-3 h-3 rounded-full bg-red-400" />
                                <span className="w-3 h-3 rounded-full bg-amber-400" />
                                <span className="w-3 h-3 rounded-full bg-green-400" />
                            </div>
                            <div className="flex-1 max-w-md flex items-center gap-2 px-3.5 py-1.5 rounded-md bg-white ring-1 ring-gray-200/80">
                                <SolarIcon
                                    name="lock-bold-duotone"
                                    className="w-3.5 h-3.5 text-gray-500 flex-shrink-0"
                                />
                                <span className="text-xs text-gray-600 font-mono">
                                    tonystark.dev
                                </span>
                            </div>
                            <div
                                className="w-[52px] flex-shrink-0"
                                aria-hidden="true"
                            />
                        </div>

                        {/* Site content */}
                        <div className="px-6 sm:px-8 py-6 sm:py-8">
                            {/* Navigation */}
                            <div className="flex justify-between items-center pb-5 border-b border-gray-100">
                                <span className="font-bold text-gray-900 text-sm sm:text-base">
                                    Tony Stark
                                </span>
                                <nav className="flex items-center gap-4 sm:gap-6 text-xs sm:text-sm text-gray-600">
                                    <span>Accueil</span>
                                    <span>Projets</span>
                                    <span>Contact</span>
                                </nav>
                            </div>

                            {/* Hero */}
                            <div className="text-center py-8 sm:py-10">
                                <div className="w-16 h-16 sm:w-20 sm:h-20 rounded-full bg-blue-100 ring-4 ring-blue-50 mx-auto flex items-center justify-center mb-4">
                                    <span className="text-blue-600 font-bold text-lg sm:text-xl">
                                        TS
                                    </span>
                                </div>
                                <h4 className="text-lg sm:text-2xl font-bold text-gray-900 mb-2 leading-tight">
                                    Bonjour, je suis{" "}
                                    <span className="text-blue-600">
                                        Tony Stark
                                    </span>
                                </h4>
                                <p className="text-[11px] sm:text-sm text-gray-600 mb-5 max-w-md mx-auto leading-relaxed">
                                    Développeur web junior passionné par la
                                    création d'interfaces simples et agréables à
                                    utiliser.
                                </p>
                                <div className="flex justify-center gap-2 sm:gap-3">
                                    <span className="bg-blue-600 text-white px-3 sm:px-4 py-1.5 sm:py-2 rounded-md text-[11px] sm:text-xs font-medium">
                                        Voir mes projets
                                    </span>
                                    <span className="border border-blue-600 text-blue-600 px-3 sm:px-4 py-1.5 sm:py-2 rounded-md text-[11px] sm:text-xs font-medium">
                                        Me contacter
                                    </span>
                                </div>
                            </div>

                            {/* Section "À propos" preview */}
                            <div className="border-t border-gray-100 pt-5">
                                <p className="text-[10px] sm:text-xs font-bold text-gray-900 mb-2">
                                    À propos de moi
                                </p>
                                <div className="space-y-1.5 mb-3">
                                    <div className="h-1.5 bg-gray-100 rounded w-full" />
                                    <div className="h-1.5 bg-gray-100 rounded w-[85%]" />
                                    <div className="h-1.5 bg-gray-100 rounded w-[60%]" />
                                </div>
                                <div className="flex flex-wrap gap-1.5">
                                    {[
                                        "HTML",
                                        "CSS",
                                        "Responsive",
                                        "Webdesign",
                                        "Git",
                                    ].map((tag) => (
                                        <span
                                            key={tag}
                                            className="text-[9px] sm:text-[10px] font-medium text-blue-600 bg-blue-50 px-2 py-0.5 rounded-full"
                                        >
                                            {tag}
                                        </span>
                                    ))}
                                </div>
                            </div>
                        </div>
                    </div>

                </div>

                {/* RIGHT — Storytelling vendeur */}
                <div>
                    <div className="inline-flex items-center gap-2 rounded-full bg-orange-500/10 dark:bg-orange-500/15 ring-1 ring-orange-500/25 dark:ring-orange-400/30 px-3 py-1 mb-4">
                        <span className="relative flex h-2 w-2">
                            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-orange-400 opacity-75" />
                            <span className="relative inline-flex rounded-full h-2 w-2 bg-orange-500" />
                        </span>
                        <span className="text-[10px] sm:text-[11px] font-semibold uppercase tracking-[0.18em] text-orange-600 dark:text-orange-300">
                            {t("PortfolioFilRougeSection_eyebrow")}
                        </span>
                    </div>

                    <h3 className="text-2xl sm:text-3xl md:text-[2rem] font-bold text-black dark:text-white leading-[1.1] tracking-tight mb-4 font-sourceSans">
                        {t.rich("PortfolioFilRougeSection_headline", {
                            highlight: (chunks) => (
                                <span className="bg-gradient-to-r from-orange-500 to-amber-500 bg-clip-text text-transparent">
                                    {chunks}
                                </span>
                            ),
                        })}
                    </h3>

                    <p className="text-sm sm:text-base text-black/70 dark:text-lightBlue/80 leading-relaxed mb-6">
                        {t("PortfolioFilRougeSection_description")}
                    </p>

                    {/* Stats / features en grille */}
                    <div className="grid grid-cols-2 gap-3 mb-6">
                        {[
                            {
                                icon: "documents-bold-duotone",
                                titleKey:
                                    "PortfolioFilRougeSection_feature_pages_title",
                                descKey:
                                    "PortfolioFilRougeSection_feature_pages_desc",
                            },
                            {
                                icon: "smartphone-bold-duotone",
                                titleKey:
                                    "PortfolioFilRougeSection_feature_responsive_title",
                                descKey:
                                    "PortfolioFilRougeSection_feature_responsive_desc",
                            },
                            {
                                icon: "palette-bold-duotone",
                                titleKey:
                                    "PortfolioFilRougeSection_feature_custom_title",
                                descKey:
                                    "PortfolioFilRougeSection_feature_custom_desc",
                            },
                            {
                                icon: "global-bold-duotone",
                                titleKey:
                                    "PortfolioFilRougeSection_feature_online_title",
                                descKey:
                                    "PortfolioFilRougeSection_feature_online_desc",
                            },
                        ].map((f) => (
                            <div
                                key={f.titleKey}
                                className="rounded-xl border border-gray-200 dark:border-borderDark/80 bg-white dark:bg-bgDark-500 p-3.5"
                            >
                                <div className="flex items-center gap-2 mb-1.5">
                                    <SolarIcon
                                        name={f.icon}
                                        className="w-4 h-4 text-orange-500"
                                    />
                                    <p className="text-[13px] font-semibold text-black dark:text-white mb-0">
                                        {t(f.titleKey)}
                                    </p>
                                </div>
                                <p className="text-[11px] text-black/60 dark:text-lightBlue/70 leading-snug mb-0">
                                    {t(f.descKey)}
                                </p>
                            </div>
                        ))}
                    </div>

                    <p className="text-xs text-black/60 dark:text-lightBlue/60 italic mb-0 leading-relaxed">
                        {t("PortfolioFilRougeSection_footnote")}
                    </p>
                </div>
            </div>
        </section>
    );
}
